import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import extractText from 'flarum/common/utils/extractText';
import SelectDropdown from 'flarum/common/components/SelectDropdown';
import LinkButton from 'flarum/common/components/LinkButton';
import m from 'mithril';

/**
 * The phone header bar's page menu for the marketing pages (/, /about,
 * /wall-of-love): the page's own name on the button, the site's main pages in
 * the list, after Home. Core lifts .App-titleControl into the middle of the bar on phones
 * and hides it on desktop; LrPage-barNav keeps it out of the page body.
 * Built like the blog's switcher (blogIndex.tsx).
 *
 * The list is the desktop header's: the links managed in the admin panel with
 * fof/links, read from the store the forum payload already filled, so editing
 * the nav there edits this menu too. Nothing is imported from fof/links (an
 * optional extension); with none installed the list is simply empty.
 */

const byPosition = (a, b) => (a.position() ?? 0) - (b.position() ?? 0);

/** Top-level links in the admin's order, each followed by its children. Headings are not destinations. */
function navLinks() {
  const all = (app.store.all('links') || []).slice().sort(byPosition);
  const out = [];
  all.filter(l => !l.parent()).forEach(l => {
    if (!l.isLabel()) out.push(l);
    all.filter(c => c.parent() === l).forEach(c => !c.isLabel() && out.push(c));
  });
  return out;
}
function href(l) {
  const url = l.url();
  return url.startsWith('/') && l.isInternal() ? app.forum.attribute('baseUrl') + url : url;
}

/** Is this the page we are on? Same test the desktop header uses. */
function isCurrent(l) {
  if (!l.isInternal()) return false;
  const base = app.forum.attribute('baseUrl');
  const here = new URL(m.route.get() || '/', base).href.replace(base, '');
  const there = new URL(href(l), base).href.replace(base, '');
  return here === there || there !== '/' && here.startsWith(there) && /^[/?#]/.test(here.charAt(there.length));
}
export default class PageNav extends Component {
  view() {
    // One flat, unkeyed list, like the blog's switcher. Dropdown wraps each
    // child in its own <li>, and keys on the children crash Mithril's redraw
    // ("reading 'onbeforeupdate'"), which took the whole site down on
    // 2026-10-30. Home leads: the header nav has no entry for it (the logo is
    // the way home there), and on the home page it is the item the button reads.
    const items = [m(LinkButton, {
      icon: "fas fa-home",
      href: app.forum.attribute('baseUrl') + '/',
      active: this.attrs.page === 'home'
    }, app.translator.trans('linkrobins-lr-home.forum.page_nav.home')), ...navLinks().map(l => m(LinkButton, {
      icon: l.icon() || undefined,
      href: href(l),
      external: !l.isNewtab() && !l.isInternal(),
      target: l.isNewtab() ? '_blank' : undefined,
      active: isCurrent(l)
    }, l.title()))];
    const label = app.translator.trans(`linkrobins-lr-home.forum.page_nav.${this.attrs.page}`);
    return m(SelectDropdown, {
      className: "App-titleControl LrPage-barNav LrHome-pageNav",
      buttonClassName: "Button",
      accessibleToggleLabel: app.translator.trans('linkrobins-lr-home.forum.page_nav.switch_label', {
        current: extractText(label)
      }),
      defaultLabel: label
    }, items);
  }
}
flarum.reg.add('linkrobins-lr-home', 'forum/components/PageNav', PageNav);